import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonSize, DropdownItem, Filter, FilterableOp, FilterableType } from '../../../models';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'vl-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterComponent implements OnInit {
  @Input() filter: Filter;
  @Input() buttonSize?: ButtonSize = ButtonSize.Default;

  isOperatorMenuOpen = false;
  operatorMenuItems: DropdownItem[];
  form: FormGroup;
  filterLabel: string;
  datePipe = new DatePipe('en-US');

  private readonly operatorsByType: Record<FilterableType, FilterableOp[]> = {
    [FilterableType.BOOLEAN]: [FilterableOp.EQ],
    [FilterableType.TEXT]: [FilterableOp.LIKE, FilterableOp.EQ, FilterableOp.NE],
    [FilterableType.INPUT]: [FilterableOp.LIKE],
    [FilterableType.NUMBER]: [FilterableOp.EQ, FilterableOp.LT, FilterableOp.LE, FilterableOp.GT, FilterableOp.GE],
    [FilterableType.DATE]: [FilterableOp.SAME, FilterableOp.EA, FilterableOp.EB, FilterableOp.AF, FilterableOp.BF]
  };

  private readonly operatorsMap: Record<FilterableOp, string> = {
    [FilterableOp.LIKE]: 'contains',
    [FilterableOp.EQ]: 'equals',
    [FilterableOp.SAME]: 'is',
    [FilterableOp.NE]: 'is not',
    [FilterableOp.LT]: 'less',
    [FilterableOp.LE]: 'less or equal',
    [FilterableOp.GT]: 'greater',
    [FilterableOp.GE]: 'greater or equal',
    [FilterableOp.EA]: 'is same or after',
    [FilterableOp.EB]: 'is same or before',
    [FilterableOp.AF]: 'is after',
    [FilterableOp.BF]: 'is before'
  };

  @Output() private filterUpdated = new EventEmitter<Filter>();
  @Output() private filterRemoved = new EventEmitter<void>();

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    const operators: FilterableOp[] = this.operatorsByType[this.filter.filterable.type];

    this.operatorMenuItems = operators.map((operator, index) => ({
      id: String(index),
      value: this.operatorsMap[operator]
    }));

    this.initForm();
    this.filterLabel = this.getFilterLabel();
  }

  onChangeOperator({ id: index }: DropdownItem): void {
    this.form.controls.operator.setValue(this.operatorMenuItems[Number(index)].value);
  }

  onFilterRemove(event: MouseEvent): void {
    this.filterRemoved.emit();
  }

  onConditionUpdate(formValue: any, dropdown: BsDropdownDirective): void {
    const operator = Object.keys(this.operatorsMap).find((key: FilterableOp) => {
      return this.operatorsMap[key] === formValue.operator;
    });

    this.filterUpdated.emit({
      ...this.filter,
      condition: {
        key: this.filter.filterable.name,
        value: formValue.value,
        operator: FilterableOp[operator as keyof typeof FilterableOp]
      }
    });

    this.filterLabel = this.getFilterLabel();

    dropdown.hide();
  }

  onClearForm(): void {
    this.form.reset();
  }

  onInitForm(): void {
    this.initForm();
  }

  private initForm(): void {
    const { condition } = this.filter;
    const value = (condition && condition.value) || '';
    const operator = (condition && this.operatorsMap[condition.operator]) || this.operatorMenuItems[0].value;

    this.form = this.formBuilder.group({
      value: [value, [Validators.required]],
      operator: [operator]
    });
  }

  private getFilterLabel(): string {
    let { label } = this.filter.filterable;
    const { hideConditionValue } = this.filter.filterable;

    if (this.filter.condition && !hideConditionValue) {
      let valueLabel = this.filter.condition.value;

      if (this.filter.filterable.type === 'DATE') {
        valueLabel = this.datePipe.transform(valueLabel, 'MM/dd/yyyy');
      }

      label += ` ${this.operatorsMap[this.filter.condition.operator]} ${valueLabel}`;
    }

    return label;
  }
}
